import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '../lib/supabase';
import { getProfile, syncOAuthProfile, PROFILE_RESULT_STATUS } from '../services/authService';
import { queryClient } from '../providers/QueryProvider';
import { clearBlockCache } from '../services/moderationService';
import { profileKeys } from '../hooks/queries/useProfileQuery';
import { getProfileScreen } from '../services/databaseService';
import { communityKeys } from '../hooks/queries/useCommunityQuery';
import { getPosts as getCommunityFeedPage } from '../services/communityService';
import { setTelemetryUser } from '../services/telemetry';
import { invalidateUserCache, setUserCache } from '../services/userCacheService';

const AuthContext = createContext({});

// Cache local do perfil (por usuário). Reidratado no boot ANTES do fetch de
// rede: offline, o usuário logado já vê nome/avatar/nível salvos da última
// sessão em vez de cair nos fallbacks ('Estudante'). Diferente do React Query
// (que também persiste o perfil via useProfileQuery), este alimenta o
// `profile` do Context — fonte usada por header/Home e pelo guard de telas.
const PROFILE_CACHE_PREFIX = '@profile_cache_v1:';
const profileCacheKey = (userId) => `${PROFILE_CACHE_PREFIX}${userId}`;

const readProfileCache = async (userId) => {
    try {
        const raw = await AsyncStorage.getItem(profileCacheKey(userId));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null; // cache corrompido/ausente nunca trava o boot
    }
};

// Fire-and-forget: persistir o perfil não deve bloquear o fluxo de auth.
const writeProfileCache = (userId, profile) => {
    if (!userId || !profile) return;
    AsyncStorage.setItem(profileCacheKey(userId), JSON.stringify(profile)).catch(() => {});
};

/**
 * Estados discriminados do ciclo de vida do perfil. Substitui o anti-pattern
 * de inferir status do shape (`profile === null` ambíguo entre "ainda
 * carregando", "carga falhou", "perfil ausente no banco").
 *
 * Transições válidas:
 *   idle      -> loading            (apply de sessão com user válido)
 *   loading   -> loaded             (getProfile OK)
 *   loading   -> not_found          (registro ausente — PGRST116)
 *   loading   -> error              (falha de rede / RLS / timeout)
 *   loaded    -> loaded             (refresh bem-sucedido)
 *   error     -> loaded             (refresh recuperou)
 *   not_found -> loaded             (perfil criado e refetch ok)
 *   *         -> idle               (logout)
 *
 * refreshProfile faz transições NÃO-degradantes: falha em refresh quando
 * profile já está 'loaded' mantém 'loaded' (dado em cache > UI piscando).
 */
export const PROFILE_STATUS = Object.freeze({
    IDLE: 'idle',
    LOADING: 'loading',
    LOADED: 'loaded',
    NOT_FOUND: 'not_found',
    ERROR: 'error',
});

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [profileStatus, setProfileStatus] = useState(PROFILE_STATUS.IDLE);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [initialized, setInitialized] = useState(false);

    // Dedup contra getSession() + INITIAL_SESSION quase concorrentes.
    // Marcação ocorre ANTES do await; reset acontece em qualquer falha
    // (throw OU null silencioso de getProfile) para permitir retry no
    // próximo evento de onAuthStateChange.
    const handledUserIdRef = useRef(null);
    // Identidade desejada mais recente. Uma resposta lenta da conta A não pode
    // sobrescrever o estado depois de logout ou login da conta B.
    const activeSessionUserIdRef = useRef(null);

    const applySession = useCallback(async (nextSession) => {
        if (!nextSession) {
            activeSessionUserIdRef.current = null;
            handledUserIdRef.current = null;
            invalidateUserCache();
            setSession(null);
            setUser(null);
            setProfile(null);
            setProfileStatus(PROFILE_STATUS.IDLE);
            // Limpa o usuário no Sentry (logout/expiração de sessão).
            setTelemetryUser(null);
            // Logout/expiração: descarta TODO o server-state em cache (memória +
            // persistido em AsyncStorage). Sem isto, dados de um usuário poderiam
            // aparecer para o próximo no mesmo aparelho. Idempotente (no boot sem
            // sessão o cache já está vazio).
            queryClient.clear();
            // Limpa o conjunto de usuários bloqueados em memória — senão a lista de um
            // usuário vazaria para o próximo login no mesmo aparelho.
            clearBlockCache();
            return;
        }

        const sessionUserId = nextSession.user.id;
        activeSessionUserIdRef.current = sessionUserId;
        setSession(nextSession);
        setUser(nextSession.user);
        // A sessão já contém o usuário necessário para todas as consultas do app.
        // Semear o cache aqui evita que services legados chamem auth.getUser()
        // (uma requisição de rede) ao abrir cada tela ou após os 5 min do cache.
        setUserCache(nextSession.user);

        if (handledUserIdRef.current === sessionUserId) return;
        handledUserIdRef.current = sessionUserId;

        setProfileStatus(PROFILE_STATUS.LOADING);

        // Reidrata o perfil salvo ANTES de tocar a rede. Offline, isto é o que o
        // usuário vê; online, evita o "piscar" de fallback enquanto o fetch corre.
        const cachedProfile = await readProfileCache(sessionUserId);
        if (activeSessionUserIdRef.current !== sessionUserId) return;
        if (cachedProfile) setProfile(cachedProfile);

        // Dispara a query do Perfil JUNTO com a leitura do perfil, não depois.
        //
        // POR QUÊ: o prefetch abaixo (no bloco de sucesso) só começava depois de
        // `getProfile` resolver — dois round-trips EM SÉRIE antes de a tela de
        // Perfil ter dados, o que em rede móvel é o dobro da latência sentida.
        // A RPC `get_profile_screen` resolve o usuário por `auth.uid()` no
        // servidor: não depende do resultado de `getProfile`, então não há razão
        // para esperar. A chave é a MESMA da tela (`profileKeys.screen`), então o
        // React Query deduplica — se a tela montar antes disto terminar, ela
        // aproveita esta requisição em vez de abrir outra.
        // Fire-and-forget: falha aqui é irrelevante (a tela refaz por conta).
        queryClient.prefetchQuery({
            queryKey: profileKeys.screen(sessionUserId),
            queryFn: () => getProfileScreen(sessionUserId),
            staleTime: 5 * 60 * 1000,
        }).catch(() => {});

        try {
            let result;

            if (Platform.OS === 'web') {
                // WEB: caminho comum = UMA leitura. O navegador móvel sofria
                // com a waterfall syncOAuthProfile (SELECT/UPDATE) -> getProfile.
                // O trigger cria perfis novos; o sync client-side fica como
                // recuperação apenas quando o registro realmente não existe.
                result = await getProfile(sessionUserId);

                if (result.status === PROFILE_RESULT_STATUS.NOT_FOUND) {
                    const syncResult = await syncOAuthProfile(nextSession.user);
                    if (syncResult.success) {
                        result = await getProfile(sessionUserId);
                    }
                }
            } else {
                // NATIVO: preserva a sequência já estabilizada no aplicativo.
                await syncOAuthProfile(nextSession.user);
                result = await getProfile(sessionUserId);
            }

            // Logout/troca de conta pode ter acontecido durante qualquer await.
            if (activeSessionUserIdRef.current !== sessionUserId) return;

            if (result.status === PROFILE_RESULT_STATUS.OK) {
                setProfile(result.data);
                setProfileStatus(PROFILE_STATUS.LOADED);
                writeProfileCache(sessionUserId, result.data);
                // Identifica o usuário no Sentry: erros futuros mostram quem foi afetado.
                setTelemetryUser({
                    id: sessionUserId,
                    email: nextSession.user.email,
                    username: result.data?.username || result.data?.nome || undefined,
                });
                // (O prefetch do Perfil subiu para ANTES do `getProfile`, para
                // os dois round-trips correrem em paralelo — ver comentário lá.)
                // Idem para a 1ª página do feed da Comunidade (aba primária). A key
                // precisa bater EXATAMENTE com a da tela (`{ topic:'all', search:'' }`).
                queryClient.prefetchInfiniteQuery({
                    queryKey: communityKeys.infinitePosts({ topic: 'all', search: '' }),
                    queryFn: ({ pageParam }) =>
                        getCommunityFeedPage({ topic: 'all', search: '', limit: 10, cursor: pageParam }),
                    initialPageParam: null,
                }).catch(() => {});
            } else if (result.status === PROFILE_RESULT_STATUS.NOT_FOUND) {
                // Estado raro mas legítimo: perfil não existe no banco
                // (deleção em andamento, syncOAuthProfile falhou em criar,
                // ou conta foi removida). Diferente de erro transitório:
                // retry imediato não resolveria. UI pode oferecer logout
                // ou criação manual de perfil baseada neste status.
                setProfileStatus(PROFILE_STATUS.NOT_FOUND);
                handledUserIdRef.current = null;
            } else {
                // Falha transitória (rede, RLS, timeout). Destrava ref guard
                // para que o próximo evento de auth (ex.: TOKEN_REFRESHED ao
                // reconectar) ou refresh manual reentre e atualize.
                // Se havia cache, NÃO degrada: dado salvo (mesmo stale) > UI
                // vazia/erro. Sem cache, mantém ERROR para sinalizar retry.
                setProfileStatus(cachedProfile ? PROFILE_STATUS.LOADED : PROFILE_STATUS.ERROR);
                handledUserIdRef.current = null;
            }
        } catch (_error) {
            // Defesa contra throw inesperado (Supabase JS pode jogar em
            // alguns cenários de auth quebrada). Mesma lógica do ramo 'error'.
            if (activeSessionUserIdRef.current !== sessionUserId) return;
            setProfileStatus(cachedProfile ? PROFILE_STATUS.LOADED : PROFILE_STATUS.ERROR);
            handledUserIdRef.current = null;
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        const deferredAuthTimers = new Set();

        const initializeAuth = async () => {
            try {
                const { data: { session: currentSession } } = await supabase.auth.getSession();
                if (isMounted) await applySession(currentSession);
            } catch (_error) {
                // supabase.auth.getSession() raramente joga. Se acontecer,
                // o caminho fica em 'idle' e o próximo evento de auth
                // (onAuthStateChange) cobre.
            } finally {
                if (isMounted) {
                    setLoading(false);
                    setInitialized(true);
                }
            }
        };

        initializeAuth();

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (_event, newSession) => {
                // IMPORTANTE: este callback precisa ser síncrono. O auth-js o
                // executa dentro de um lock exclusivo e aguarda a Promise do
                // subscriber; fazer queries Supabase em applySession antes de
                // devolver o lock causa deadlock no browser (inclusive entre
                // abas). Adiar para a próxima macrotask libera o lock primeiro.
                const timerId = setTimeout(() => {
                    deferredAuthTimers.delete(timerId);
                    if (!isMounted) return;

                    applySession(newSession)
                        .catch(() => {})
                        .finally(() => {
                            if (isMounted) setLoading(false);
                        });
                }, 0);
                deferredAuthTimers.add(timerId);
            }
        );

        return () => {
            isMounted = false;
            deferredAuthTimers.forEach(clearTimeout);
            deferredAuthTimers.clear();
            subscription?.unsubscribe();
        };
    }, [applySession]);

    /**
     * Refresh idempotente do perfil. Pode ser chamado livremente
     * (ex.: useFocusEffect, retry manual). NÃO degrada o status:
     * falha em refresh mantém o estado anterior — preferimos dado
     * em cache potencialmente stale a UI piscando para erro.
     */
    const refreshProfile = useCallback(async () => {
        if (!user?.id) return;
        const result = await getProfile(user.id);
        if (result.status === PROFILE_RESULT_STATUS.OK) {
            setProfile(result.data);
            setProfileStatus(PROFILE_STATUS.LOADED);
            writeProfileCache(user.id, result.data);
            handledUserIdRef.current = user.id;
        }
        // NOT_FOUND e ERROR: mantém status atual (não-degradante).
        // Próximo evento de auth ou próximo focus reentra.
    }, [user]);

    const value = {
        user,
        profile,
        profileStatus,
        session,
        loading,
        initialized,
        isAuthenticated: !!session,
        refreshProfile,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
